/* JSON-LD Recipe schema injector.
 *
 * Renders nothing visible — instead, on mount it pushes a single
 * <script type="application/ld+json"> block into <head> describing the
 * recipe in schema.org/Recipe format. This makes the page eligible for
 * Google's Rich Results (the photo + cook time + rating card that shows
 * up directly in search results) and for AI search engines that index
 * structured data.
 *
 * Only emit when the recipe is PUBLIC — there's no value injecting schema
 * for private recipes, and we don't want them surfaced in search.
 *
 * Bonus: also sets a <link rel="canonical"> pointing at the SEO-slug URL
 * so any visit via the legacy /r/rcp_xxx URL still tells Google the
 * canonical address is /r/{slug}-{id}.
 */
import { useEffect } from "react";
import { recipePath } from "@/lib/slug";
import { useSiteFlags } from "@/contexts/SiteFlagsContext";
import { buildRecipeMetaDescription } from "@/lib/seoText";

// ISO 8601 duration helper — schema.org wants e.g. "PT15M" or "PT1H30M".
function toIsoDuration(hours, minutes) {
  const h = Number(hours) || 0;
  const m = Number(minutes) || 0;
  if (!h && !m) return undefined;
  let s = "PT";
  if (h) s += `${h}H`;
  if (m) s += `${m}M`;
  return s;
}

function buildRecipeSchema(recipe, canonical, brandFallbackImage, nutritionOn) {
  const ingredients = (recipe.ingredients || [])
    .map((ing) => {
      // Ingredient model is {name, quantity, unit, notes}. We render
      // "quantity unit name (notes)" — same shape humans say out loud.
      if (!ing || typeof ing !== "object") return String(ing || "");
      const qty = ing.quantity !== undefined && ing.quantity !== null && ing.quantity !== "" ? String(ing.quantity) : "";
      const parts = [qty, ing.unit, ing.name].filter(Boolean).map(String);
      const base = parts.join(" ").trim();
      return ing.notes ? `${base} (${ing.notes})` : base;
    })
    .filter(Boolean);

  const instructions = (recipe.instructions || [])
    .filter((step) => typeof step === "string" && step.trim())
    .map((step, idx) => {
      // Derive a short `name` from the first ~60 chars of the step text.
      // Google's Rich Results validator flags absent step names as a
      // warning. Absolute-URL `url` deep-links to the step anchor on the
      // recipe page.
      const raw = step.trim();
      const shortName = raw.length <= 60 ? raw : raw.slice(0, 60).replace(/\s\S*$/, "") + "…";
      return {
        "@type": "HowToStep",
        position: idx + 1,
        name: `Step ${idx + 1}: ${shortName}`,
        text: raw,
        url: `${canonical}#step-${idx + 1}`,
      };
    });

  const prep = toIsoDuration(recipe.prep_hours, recipe.prep_minutes);
  const cook = toIsoDuration(recipe.cook_hours, recipe.cook_minutes);
  // schema.org's `totalTime` = the FULL wall-clock time to make the dish
  // (prep + cook + rest / marinate / chill). Even though our on-page "Total
  // time" label displays prep+cook only (the active kitchen time), Google's
  // rich result spec expects the true elapsed time so cooks reading a
  // search-result card know a "20-min prep" bread really needs 3 hours to
  // rise. `restTime` isn't in schema.org so we roll it into totalTime only.
  const total = toIsoDuration(
    (Number(recipe.prep_hours) || 0) + (Number(recipe.cook_hours) || 0),
    (Number(recipe.prep_minutes) || 0)
      + (Number(recipe.cook_minutes) || 0)
      + (Number(recipe.rest_minutes) || 0),
  );

  // Image fallback chain (mirrors backend SSR at
  // `/app/backend/routes/public.py:render_recipe_html`).
  //   1. AI-generated hero when the cook has activated it
  //   2. Uploaded photos on the recipe
  //   3. Site brand logo — recipes with zero photos still pass Google's
  //      "image is required" Rich Results validator this way. Without
  //      this fallback the page gets rejected for indexing entirely.
  const isVideo = (u) => /\.(mov|mp4|webm)(\?|$)/i.test(u || "");
  const toAbs = (u) => {
    if (!u) return null;
    if (/^https?:\/\//i.test(u)) return u;
    if (u.startsWith("/")) return window.location.origin + u;
    return u;
  };
  const imageCandidates = [];
  if (recipe.ai_image_active && recipe.ai_image_url) imageCandidates.push(toAbs(recipe.ai_image_url));
  (recipe.images || []).forEach((im) => imageCandidates.push(toAbs(im)));
  let images = imageCandidates.filter(Boolean).filter((u) => !isVideo(u));
  if (images.length === 0) {
    // Branded fallback so every public recipe has a valid `image` and
    // passes Google Rich Results (see backend counterpart in
    // /app/backend/routes/public.py `render_recipe_html`).
    images = [toAbs("/recipe-default.jpg")];
  }

  // recipeCategory + recipeCuisine — derived from tags. Optional per
  // schema.org but Google Rich Results scoring favours their presence.
  const tagsLc = (recipe.tags || []).map((t) => String(t).toLowerCase());
  const KNOWN_CUISINES = new Set([
    "italian", "french", "chinese", "indian", "thai", "japanese", "mexican",
    "spanish", "greek", "irish", "british", "american", "vietnamese",
    "korean", "turkish", "moroccan", "lebanese", "german", "polish",
    "portuguese", "brazilian", "caribbean", "middle eastern", "mediterranean",
    "cajun", "creole", "ethiopian", "malaysian", "indonesian", "filipino",
  ]);
  const cap = (s) => s.replace(/\b\w/g, (c) => c.toUpperCase());
  const recipeCuisine = (recipe.cuisine || "").trim() || tagsLc.find((t) => KNOWN_CUISINES.has(t));
  const recipeCategory =
    recipe.section ||
    (tagsLc.find((t) => !KNOWN_CUISINES.has(t)) || null);

  // AggregateRating — eligible for the gold-star treatment in Google
  // results. Only emit when at least one rating exists so we never lie
  // to crawlers about an unrated recipe.
  let aggregateRating;
  const ratingCount = Number(recipe.rating_count) || 0;
  const ratingSum = Number(recipe.rating_sum) || 0;
  if (ratingCount > 0 && ratingSum > 0) {
    const ratingValue = (ratingSum / ratingCount).toFixed(1);
    aggregateRating = {
      "@type": "AggregateRating",
      ratingValue,
      ratingCount,
      bestRating: "5",
      worstRating: "1",
    };
  }

  // VideoObject — when the recipe has a linked demo video, surface it so
  // Google can index it in the dedicated "Video" tab.
  let video;
  const videoUrl = recipe.video_url || imageCandidates.filter(Boolean).find(isVideo);
  if (videoUrl) {
    const ytId =
      (videoUrl.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/) || [])[1];
    video = {
      "@type": "VideoObject",
      name: `${recipe.title || "Recipe"} — demo`,
      description: buildRecipeMetaDescription(recipe) || `Watch how to make ${recipe.title || "this recipe"}.`,
      thumbnailUrl: ytId ? [`https://i.ytimg.com/vi/${ytId}/hqdefault.jpg`] : (images.length ? [images[0]] : undefined),
      uploadDate: recipe.created_at,
      contentUrl: videoUrl,
      embedUrl: ytId ? `https://www.youtube.com/embed/${ytId}` : videoUrl,
    };
    Object.keys(video).forEach((k) => video[k] === undefined && delete video[k]);
  }

  // Google wants an image or video on every step; the main photo is the honest fallback.
  instructions.forEach((st) => { st.image = images[0]; });

  const schema = {
    "@context": "https://schema.org",
    "@type": "Recipe",
    name: recipe.title,
    image: images.length ? images : undefined,
    author: {
      "@type": "Person",
      name: recipe.creator_name || "Recipe Filer cook",
    },
    description: buildRecipeMetaDescription(recipe) || `${recipe.title} — preserved in Recipe Filer.`,
    datePublished: recipe.created_at,
    dateModified: recipe.updated_at,
    recipeYield: recipe.serves ? String(recipe.serves) : undefined,
    recipeCategory: recipeCategory ? cap(recipeCategory) : undefined,
    recipeCuisine: recipeCuisine ? cap(recipeCuisine) : undefined,
    keywords: Array.isArray(recipe.tags) && recipe.tags.length ? recipe.tags.join(", ") : undefined,
    prepTime: prep,
    cookTime: cook,
    totalTime: total,
    recipeIngredient: ingredients.length ? ingredients : undefined,
    recipeInstructions: instructions.length ? instructions : undefined,
    aggregateRating,
    video,
    publisher: {
      "@type": "Organization",
      name: "Recipe Filer",
      url: "https://recipefiler.com",
    },
    url: canonical,
    mainEntityOfPage: canonical,
    nutrition: recipe.nutrition?.kcal && recipe.show_nutrition !== false && nutritionOn ? Object.fromEntries(Object.entries({
      "@type": "NutritionInformation",
      calories: `${recipe.nutrition.kcal} calories`,
      proteinContent: recipe.nutrition.protein_g != null ? `${recipe.nutrition.protein_g} g` : undefined,
      carbohydrateContent: recipe.nutrition.carbs_g != null ? `${recipe.nutrition.carbs_g} g` : undefined,
      fatContent: recipe.nutrition.fat_g != null ? `${recipe.nutrition.fat_g} g` : undefined,
      fiberContent: recipe.nutrition.fibre_g != null ? `${recipe.nutrition.fibre_g} g` : undefined,
      sugarContent: recipe.nutrition.sugar_g != null ? `${recipe.nutrition.sugar_g} g` : undefined,
      sodiumContent: recipe.nutrition.sodium_mg != null ? `${recipe.nutrition.sodium_mg} mg` : undefined,
    }).filter(([, v]) => v !== undefined)) : undefined,
    speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", ".recipe-ingredients", ".recipe-instructions"] },
  };

  // Drop undefined keys so the JSON stays compact + valid
  Object.keys(schema).forEach((k) => schema[k] === undefined && delete schema[k]);
  return schema;
}

/* Idempotent DOM writer — safe to call during render AND inside useEffect
 * so crawler-cache snapshots (which run after commit but before useEffect)
 * pick up canonical + JSON-LD + OG immediately. */
function applyRecipeMeta(recipe, brandFallbackImage, nutritionOn = true) {
  if (typeof document === "undefined") return;
  if (!recipe || !recipe.is_public) return;

  const canonical = `${window.location.origin}${recipePath(recipe)}`;
  const schema = buildRecipeSchema(recipe, canonical, brandFallbackImage, nutritionOn);

  const setScript = (id, content) => {
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("script");
      el.id = id;
      el.type = "application/ld+json";
      document.head.appendChild(el);
    }
    el.text = content;
  };
  setScript("rf-recipe-jsonld", JSON.stringify(schema));

  const trail = [
    { name: "Recipe Filer", url: window.location.origin + "/" },
    { name: "Directory", url: window.location.origin + "/directory" },
  ];
  if (recipe.cookbook_id && recipe.cookbook_title) {
    trail.push({ name: recipe.cookbook_title, url: `${window.location.origin}/cb/${recipe.cookbook_id}` });
  }
  trail.push({ name: recipe.title, url: canonical });
  setScript(
    "rf-recipe-breadcrumb",
    JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: trail.map((t, idx) => ({
        "@type": "ListItem",
        position: idx + 1,
        name: t.name,
        item: t.url,
      })),
    }),
  );

  /* Canonical — the single most important tag for the "Duplicate without
   * user-selected canonical" warning in Google Search Console. Setting it
   * during render (not just in useEffect) ensures the crawler snapshot
   * catches it before any DOM serialisation.
   * Also remove any stale generic canonical PageMeta may have written for
   * the same route so we end up with exactly ONE canonical link. */
  const stale = document.head.querySelectorAll('link[rel="canonical"]');
  stale.forEach((el) => { if (el.id !== "rf-recipe-canonical") el.remove(); });
  let canonicalEl = document.getElementById("rf-recipe-canonical");
  if (!canonicalEl) {
    canonicalEl = document.createElement("link");
    canonicalEl.id = "rf-recipe-canonical";
    canonicalEl.rel = "canonical";
    document.head.appendChild(canonicalEl);
  }
  canonicalEl.href = canonical;

  let primaryImage = "";
  if (recipe.ai_image_active && recipe.ai_image_url) {
    primaryImage = /^https?:\/\//i.test(recipe.ai_image_url)
      ? recipe.ai_image_url
      : window.location.origin + recipe.ai_image_url;
  } else {
    const firstUploaded = (recipe.images || []).find((u) => typeof u === "string" && u.length > 0);
    if (firstUploaded) {
      primaryImage = /^https?:\/\//i.test(firstUploaded) ? firstUploaded : window.location.origin + firstUploaded;
    }
  }
  /* Use the same clean description everywhere so search snippets and
   * social previews never surface raw markdown. Falls back through the
   * seoText helper chain (parsed_text → ingredients → title). */
  const cleanDesc = buildRecipeMetaDescription(recipe);
  const altText = recipe.image_alt || recipe.title || "Recipe Filer";
  const metaTags = [
    { property: "og:type", content: "article" },
    { property: "og:title", content: recipe.seo_title || recipe.title || "Recipe Filer" },
    { property: "og:description", content: cleanDesc },
    { property: "og:url", content: canonical },
    { property: "og:image", content: primaryImage },
    { property: "og:image:alt", content: primaryImage ? altText : undefined },
    { property: "og:site_name", content: "Recipe Filer" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: recipe.seo_title || recipe.title || "Recipe Filer" },
    { name: "twitter:description", content: cleanDesc },
    { name: "twitter:image", content: primaryImage },
    { name: "twitter:image:alt", content: primaryImage ? altText : undefined },
  ];
  metaTags.forEach((m) => {
    if (!m.content) return;
    const key = m.property || m.name;
    const same = document.head.querySelectorAll(m.property ? `meta[property="${key}"]` : `meta[name="${key}"]`);
    same.forEach((x, i) => { if (i > 0) x.remove(); });
    let el = same[0] || null;
    if (el) el.setAttribute("data-rf", "1");
    if (!el) {
      el = document.createElement("meta");
      if (m.property) el.setAttribute("property", m.property);
      if (m.name) el.setAttribute("name", m.name);
      el.setAttribute("data-rf", "1");
      document.head.appendChild(el);
    }
    el.setAttribute("content", m.content);
  });
}

export default function RecipeJsonLd({ recipe }) {
  const { settings, flags } = useSiteFlags();
  const nutritionOn = flags?.nutrition_estimates_enabled !== false;
  const brandFallbackImage = settings?.brand_logo_url || settings?.hero_image_url || null;

  /* Sync-during-render write so bots' DOM snapshot picks up canonical + JSON-LD. */
  applyRecipeMeta(recipe, brandFallbackImage, nutritionOn);

  useEffect(() => {
    applyRecipeMeta(recipe, brandFallbackImage, nutritionOn);
    return () => {
      /* Clean up on unmount so a subsequent non-recipe page doesn't inherit
       * stale schema. */
      ["rf-recipe-jsonld", "rf-recipe-breadcrumb", "rf-recipe-canonical"].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
      document.head.querySelectorAll('meta[data-rf="1"]').forEach((el) => el.remove());
    };
  }, [recipe, brandFallbackImage, nutritionOn]);

  return null;
}
